var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import PublicIcon from '@mui/icons-material/Public';
import TranslateIcon from '@mui/icons-material/Translate';
import { useTranslation } from 'react-i18next';
import { countryLanguageOptions } from './countryLanguageOptions';
/** Shared flag presentation. Callers own stored-code conventions and legacy aliases. */
var CountryLanguagePicker = function (props) {
    var _a;
    var _b = useTranslation(), t = _b.t, i18n = _b.i18n;
    var locale = i18n.resolvedLanguage || i18n.language || 'en';
    var label = (_a = props.label) !== null && _a !== void 0 ? _a : t(props.kind === 'language' ? 'Language' : 'Country');
    var catalogue = useMemo(function () { return countryLanguageOptions(props.kind, locale); }, [props.kind, locale]);
    var byCode = useMemo(function () { return new Map(catalogue.map(function (option) { return [option.code, option]; })); }, [catalogue]);
    var options = useMemo(function () {
        var stored = props.multiple ? props.value : [];
        return __spreadArray(__spreadArray([], catalogue.map(function (option) { return option.code; }), true), stored.filter(function (code) { return !byCode.has(code.toLowerCase()); }), true);
    }, [catalogue, byCode, props.multiple, props.value]);
    var optionFor = function (code) { return byCode.get(code.toLowerCase()); };
    var optionLabel = function (code) { var _a; return ((_a = optionFor(code)) === null || _a === void 0 ? void 0 : _a.label) || code; };
    var filterOptions = useMemo(function () { return createFilterOptions({
        stringify: function (code) {
            var _a, _b, _c;
            var option = byCode.get(code);
            return "".concat(code, " ").concat((_a = option === null || option === void 0 ? void 0 : option.label) !== null && _a !== void 0 ? _a : '', " ").concat((_b = option === null || option === void 0 ? void 0 : option.englishName) !== null && _b !== void 0 ? _b : '', " ").concat((_c = option === null || option === void 0 ? void 0 : option.nativeName) !== null && _c !== void 0 ? _c : '');
        },
    }); }, [byCode]);
    var flag = function (code) {
        var _a;
        return (_jsx(Avatar, __assign({ "aria-hidden": true, src: (_a = optionFor(code)) === null || _a === void 0 ? void 0 : _a.flag, alt: "", sx: { width: 18, height: 18, flexShrink: 0 } }, { children: props.kind === 'language' ? _jsx(TranslateIcon, { sx: { fontSize: 14 } }) : _jsx(PublicIcon, { sx: { fontSize: 14 } }) })));
    };
    var changeSingleValue = function (next) {
        var _a, _b;
        if (props.multiple)
            return;
        // An untouched input can blur with its display name. Keep the original code (including
        // its case), and allow literal prefixes or automation references in single-value rules.
        if (next === optionLabel(props.value))
            return;
        var namedOption = next ? catalogue.find(function (option) { return [option.label, option.englishName, option.nativeName]
            .some(function (name) { return (name === null || name === void 0 ? void 0 : name.toLowerCase()) === next.toLowerCase(); }); }) : undefined;
        var value = (_b = (_a = namedOption === null || namedOption === void 0 ? void 0 : namedOption.code) !== null && _a !== void 0 ? _a : next) !== null && _b !== void 0 ? _b : '';
        var sameCode = optionFor(value) && value.toLowerCase() === props.value.toLowerCase();
        if (!sameCode && value !== props.value)
            props.onChange(value);
    };
    return (_jsx(Autocomplete, { size: "small", fullWidth: true, multiple: props.multiple === true, freeSolo: !props.multiple, autoSelect: !props.multiple, forcePopupIcon: true, disableCloseOnSelect: props.multiple === true, openOnFocus: true, limitTags: 1, disabled: props.disabled, options: options, filterOptions: filterOptions, groupBy: function (code) { var _a, _b; return t((_b = (_a = optionFor(code)) === null || _a === void 0 ? void 0 : _a.group) !== null && _b !== void 0 ? _b : 'Other'); }, value: props.multiple ? props.value : props.value || null, getOptionLabel: optionLabel, isOptionEqualToValue: function (option, candidate) { return option.toLowerCase() === candidate.toLowerCase(); }, onChange: function (_event, next) {
            if (props.multiple) {
                if (Array.isArray(next))
                    props.onChange(next);
            }
            else if (!Array.isArray(next)) {
                changeSingleValue(next);
            }
        }, ListboxProps: { style: { maxHeight: 320 } }, renderOption: function (optionProps, code, _a) {
            var selected = _a.selected;
            var _b = optionProps, _key = _b.key, liProps = __rest(_b, ["key"]);
            return (_jsxs(Box, __assign({ component: "li" }, liProps, { sx: { gap: 1, overflowWrap: 'anywhere' } }, { children: [props.multiple && _jsx(Checkbox, { size: "small", checked: selected, tabIndex: -1, disableRipple: true, inputProps: { 'aria-hidden': true }, sx: { p: 0 } }), flag(code), optionLabel(code)] }), code));
        }, renderTags: function (values, getTagProps) { return values.map(function (code, index) {
            var _a = getTagProps({ index: index }), key = _a.key, tagProps = __rest(_a, ["key"]);
            return _jsx(Chip, __assign({}, tagProps, { size: "small", avatar: flag(code), label: optionLabel(code), title: optionLabel(code), sx: { maxWidth: '100%' } }), key);
        }); }, renderInput: function (params) { return (_jsx(TextField, __assign({}, params, { hiddenLabel: true, placeholder: props.multiple && props.value.length ? undefined : label, inputProps: __assign(__assign({}, params.inputProps), { 'aria-label': label }), InputProps: __assign(__assign({}, params.InputProps), { startAdornment: !props.multiple && optionFor(props.value)
                    ? flag(props.value) : params.InputProps.startAdornment }) }))); }, sx: { minWidth: 0, '& .MuiOutlinedInput-root': __assign({ minHeight: 40 }, (!props.multiple && { height: 40, py: 0 })) } }));
};
export default CountryLanguagePicker;
